import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { services } from '@/data/services';
import { servicePillars } from '@/data/servicePillars';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const inp = "w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937]";
const ta = `${inp} resize-none`;

export default function AdminServices() {
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [pillarExpanded, setPillarExpanded] = useState(null);
  const [pillarDrafts, setPillarDrafts] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/services`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        const map = {};
        const items = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : []);
        items.forEach(item => { if (item.slug) map[item.slug] = item; });

        // Init individual service drafts
        const d = {};
        services.forEach(s => {
          const ov = map[s.slug] || {};
          d[s.slug] = {
            title: ov.title || s.title,
            tagline: ov.tagline || s.tagline,
            description: ov.description || s.description,
            seo_title: ov.seo_title || s.seoTitle,
            seo_description: ov.seo_description || s.seoDesc,
            faqs: ov.faqs || s.faqs || [],
          };
        });
        setDrafts(d);

        // Init pillar drafts
        const pd = {};
        servicePillars.forEach(p => {
          const ov = map[p.slug] || {};
          pd[p.slug] = {
            title: ov.title || p.title,
            tagline: ov.tagline || p.tagline,
            description: ov.description || p.description,
            seo_title: ov.seo_title || p.seoTitle,
            seo_description: ov.seo_description || p.seoDesc,
          };
        });
        setPillarDrafts(pd);
      })
      .catch(() => toast.error('Failed to load services'))
      .finally(() => setLoading(false));
  }, []);

  const update = (slug, key, val) => setDrafts(d => ({ ...d, [slug]: { ...d[slug], [key]: val } }));
  const updatePillar = (slug, key, val) => setPillarDrafts(d => ({ ...d, [slug]: { ...d[slug], [key]: val } }));
  const updateFaq = (slug, idx, field, val) => {
    setDrafts(d => {
      const faqs = [...(d[slug]?.faqs || [])];
      faqs[idx] = { ...faqs[idx], [field]: val };
      return { ...d, [slug]: { ...d[slug], faqs } };
    });
  };

  const handleSave = async (slug) => {
    setSaving(slug);
    try {
      await axios.put(`${API}/admin/services/${slug}`, drafts[slug], { withCredentials: true, headers: getHeaders() });
      toast.success('Service saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  const handlePillarSave = async (slug) => {
    setSaving(`pillar-${slug}`);
    try {
      await axios.put(`${API}/admin/services/${slug}`, pillarDrafts[slug], { withCredentials: true, headers: getHeaders() });
      toast.success('Service pillar saved');
    } catch { toast.error('Failed to save pillar'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="w-6 h-6 animate-spin text-[#0E5A57]" /></div>;

  return (
    <div className="space-y-10 max-w-4xl" data-testid="admin-services-page">
      <div>
        <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Services</h1>
        <p className="text-sm text-[#6B7280] font-sans mt-1">Manage service pillars and individual services. Changes are reflected on the public website.</p>
      </div>

      {/* ── Service Pillars ── */}
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <Layers className="w-4 h-4 text-[#0E5A57]" />
          <h2 className="font-sans text-sm font-bold text-[#153E5C] uppercase tracking-wider">Service Pillars (6)</h2>
        </div>
        <p className="text-xs text-[#9CA3AF] mb-4 font-sans">The six major service categories shown on the Services page and individual pillar detail pages.</p>

        <div className="space-y-3">
          {servicePillars.map((pillar) => {
            const { slug } = pillar;
            const draft = pillarDrafts[slug] || {};
            const isOpen = pillarExpanded === slug;
            const isSaving = saving === `pillar-${slug}`;
            const Icon = pillar.icon;

            return (
              <div key={slug} className="bg-white border border-[#0E5A57]/15 rounded-xl overflow-hidden">
                <button type="button" onClick={() => setPillarExpanded(isOpen ? null : slug)}
                  className="w-full flex items-center justify-between p-5 hover:bg-teal-50/30 transition-colors"
                  data-testid={`pillar-item-${slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0E5A57]/8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#0E5A57]" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-[#1F2937] font-sans text-sm">{draft.title || pillar.title}</p>
                      <p className="text-xs text-[#9CA3AF] font-mono">/services/{slug}</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />}
                </button>

                {isOpen && (
                  <div className="border-t border-[#E5E7EB] p-5 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Title</label>
                        <input className={inp} value={draft.title || ''} onChange={e => updatePillar(slug, 'title', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Tagline</label>
                        <input className={inp} value={draft.tagline || ''} onChange={e => updatePillar(slug, 'tagline', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Description</label>
                      <textarea className={ta} rows={3} value={draft.description || ''} onChange={e => updatePillar(slug, 'description', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">SEO Title</label>
                        <input className={inp} value={draft.seo_title || ''} onChange={e => updatePillar(slug, 'seo_title', e.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">SEO Description</label>
                        <textarea className={ta} rows={2} value={draft.seo_description || ''} onChange={e => updatePillar(slug, 'seo_description', e.target.value)} />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button type="button" onClick={() => handlePillarSave(slug)}
                        disabled={isSaving}
                        className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#0a4a47] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
                        {isSaving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isSaving ? 'Saving…' : 'Save Pillar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Individual Services ── */}
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <h2 className="font-sans text-sm font-bold text-[#153E5C] uppercase tracking-wider">Individual Services (10)</h2>
        </div>
        <p className="text-xs text-[#9CA3AF] mb-4 font-sans">The ten individual service detail pages accessible at /services/[slug].</p>

      <div className="space-y-3">
        {services.map((service) => {
          const { slug } = service;
          const draft = drafts[slug] || {};
          const isOpen = expanded === slug;
          const isSaving = saving === slug;
          const Icon = service.icon;

          return (
            <div key={slug} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
              <button type="button" onClick={() => setExpanded(isOpen ? null : slug)}
                className="w-full flex items-center justify-between p-5 hover:bg-[#F7FAFC] transition-colors"
                data-testid={`service-item-${slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${service.color}18` }}>
                    <Icon className="w-4 h-4" style={{ color: service.color }} />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-[#1F2937] font-sans text-sm">{draft.title || service.title}</p>
                    <p className="text-xs text-[#9CA3AF] font-mono">/services/{slug}</p>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />}
              </button>

              {isOpen && (
                <div className="border-t border-[#E5E7EB] p-5 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Title</label>
                      <input className={inp} value={draft.title || ''} onChange={e => update(slug, 'title', e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Tagline / Subtitle</label>
                      <input className={inp} value={draft.tagline || ''} onChange={e => update(slug, 'tagline', e.target.value)} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">Description</label>
                    <textarea className={ta} rows={4} value={draft.description || ''} onChange={e => update(slug, 'description', e.target.value)} />
                  </div>

                  <div className="space-y-4 pt-4 border-t border-[#F3F4F6]">
                    <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">SEO</p>
                    <div className="space-y-1.5">
                      <label className="block text-xs text-[#6B7280] font-sans">SEO Title <span className="text-[#9CA3AF]">(50–60 chars recommended)</span></label>
                      <input className={inp} value={draft.seo_title || ''} onChange={e => update(slug, 'seo_title', e.target.value)} />
                      <p className="text-xs text-[#9CA3AF] font-sans">{(draft.seo_title || '').length} chars</p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs text-[#6B7280] font-sans">SEO Description <span className="text-[#9CA3AF]">(150–160 chars recommended)</span></label>
                      <textarea className={ta} rows={2} value={draft.seo_description || ''} onChange={e => update(slug, 'seo_description', e.target.value)} />
                      <p className="text-xs text-[#9CA3AF] font-sans">{(draft.seo_description || '').length} chars</p>
                    </div>
                  </div>

                  {draft.faqs && draft.faqs.length > 0 && (
                    <div className="space-y-3 pt-4 border-t border-[#F3F4F6]">
                      <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide font-sans">FAQs</p>
                      {draft.faqs.map((faq, idx) => (
                        <div key={idx} className="space-y-2 p-4 bg-[#F7FAFC] rounded-lg border border-[#E5E7EB]">
                          <div className="space-y-1">
                            <label className="block text-xs text-[#6B7280] font-sans font-medium">Q{idx + 1}. Question</label>
                            <input className={inp} value={faq.q || ''} onChange={e => updateFaq(slug, idx, 'q', e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <label className="block text-xs text-[#6B7280] font-sans font-medium">Answer</label>
                            <textarea className={`${ta} bg-white`} rows={2} value={faq.a || ''} onChange={e => updateFaq(slug, idx, 'a', e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <button onClick={() => handleSave(slug)} disabled={isSaving}
                      data-testid={`service-save-${slug}`}
                      className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                      {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
